import { useEffect, useState, useRef, useCallback } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import SideBar from '../../components/global/SideBar';
import './index.css';
import { fetchUserClasses } from '../../services/class.service';
import { fetchUserFavorites, removeFromFavorites } from '../../services/schedule.service';
import { TABS, Tabs } from '../../components/my-classes/Tabs';
import { CalendarView } from '../../components/my-classes/CalendarView';
import { FavoriteClassRow } from '../../components/my-classes/FavoriteClassRow';
import { UserClassRow } from '../../components/my-classes/UserClassRow';
import { Loader } from '@beholos/holoscommon';
import NoClasses from '../../components/classes/NoClasses';
import type { UserClassInterface } from '../../types/userClass.interface';

interface FavoriteClass {
  _id: string;
  class: {
    _id: string;
    title: string;
    slug: string;
    studio: {
      name: string;
      slug: string;
    };
  };
}

export default function MyClasses() {
  usePageTitle('Mis Clases - Holos');
  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [classes, setClasses] = useState<UserClassInterface[]>([]);
  const [favorites, setFavorites] = useState<FavoriteClass[]>([]);
  const [date, setDate] = useState<Date | null>(new Date());
  const [loading, setLoading] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(false);

  // Metadata state for tab counts
  const [metadata, setMetadata] = useState({
    userClassesAttendedCount: 0,
    userClassesPastCount: 0,
    userClassesUpcomingCount: 0,
  });

  // Pagination state - only used for "Pasadas" tab
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Refs for intersection observer
  const listContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const currentPageRef = useRef(1);

  // Helper function to check if current tab should use pagination
  const shouldUsePagination = () => activeTab === 'Pasadas';

  const loadClasses = useCallback(async (isMore = false) => {
    // Only allow pagination for "Pasadas" tab
    if (isMore && !shouldUsePagination()) {
      return;
    }

    if (isMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      currentPageRef.current = 1;
      setClasses([]);
    }

    let filters: any = {};

    // Only add pagination filters for "Pasadas" tab
    if (shouldUsePagination()) {
      filters = { page: isMore ? currentPageRef.current + 1 : 1, limit: 20 };
    }

    if (activeTab === 'Próximas') {
      filters = { ...filters, future: true };
    } else if (activeTab === 'Pasadas') {
      filters = { ...filters, past: true };
    } else if (activeTab === 'Calendario') {
      const safeDate = date ?? new Date();
      const start = new Date(safeDate.getFullYear(), safeDate.getMonth(), 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(safeDate.getFullYear(), safeDate.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
      filters = { ...filters, start, end };
    }

    try {
      const result = await fetchUserClasses(filters);
      // Update metadata from API response
      setMetadata(result.metadata);

      if (isMore && shouldUsePagination()) {
        setClasses(prev => {
          // Filter out duplicates before adding new classes
          const existingIds = new Set(prev.map(c => c._id));
          const newClasses = result.classes.filter(c => !existingIds.has(c._id));
          return [...prev, ...newClasses];
        });
        currentPageRef.current += 1;
      } else {
        setClasses(result.classes);
        currentPageRef.current = 1;
      }

      // Only set hasMore for paginated tabs
      if (shouldUsePagination()) {
        setHasMore(result.classes.length > 0);
      } else {
        setHasMore(false); // No pagination for other tabs
      }
    } catch (error) {
      console.error('Error loading classes:', error);
      // Set hasMore to false on error to prevent infinite retries
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeTab, date]);

  const loadFavorites = useCallback(async () => {
    setFavoritesLoading(true);
    try {
      const result = await fetchUserFavorites();
      setFavorites(result || []);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setFavoritesLoading(false);
    }
  }, []);

  // Intersection Observer for infinite scroll - only for "Pasadas" tab
  useEffect(() => {
    const container = listContainerRef.current;
    const sentinel = sentinelRef.current;

    if (!container || !sentinel || !shouldUsePagination()) return;

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          // Add a small delay to prevent rapid API calls
          setTimeout(() => {
            if (hasMore && !loadingMore && !loading) {
              loadClasses(true);
            }
          }, 100);
        }
      },
      {
        root: container,
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, activeTab, loadClasses]);

  useEffect(() => {
    if (activeTab === 'Favoritas') {
      loadFavorites();
    } else {
      loadClasses(false);
    }
  }, [activeTab, date, loadClasses, loadFavorites]);

  // Reset pagination when date changes (for calendar)
  useEffect(() => {
    if (activeTab === 'Calendario' && date) {
      currentPageRef.current = 1;
      setClasses([]);
      setHasMore(false); // No pagination for calendar
    }
  }, [date, activeTab]);

  const handleUnfavorite = async (classId: string) => {
    try {
      const success = await removeFromFavorites(classId);
      if (success) {
        // Remove the favorite from the local state
        setFavorites(prev => prev.filter(fav => fav.class._id !== classId));
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  return (
    <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
      <div className="hidden md:block md:w-80 lg:w-96 h-full overflow-y-auto">
        <SideBar />
      </div>

      <div className="flex-1 h-full w-full overflow-y-auto bg-gray-200 ">
        <div className="mx-auto">
          <Tabs
            active={activeTab}
            onChange={setActiveTab}
            counts={{
              upcoming: metadata.userClassesUpcomingCount,
              past: metadata.userClassesPastCount,
              attended: metadata.userClassesAttendedCount,
            }}
          />
          <div className="mt-20 space-y-4 px-6" ref={listContainerRef}>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader />
              </div>
            ) : (
              <>
                {activeTab === 'Próximas' &&
                  (classes.length > 0
                    ? (
                      <>
                        {classes.map((_class, index) => <UserClassRow key={`${_class._id}-${index}`} data={_class} />)}
                      </>
                    )
                    : <NoClasses />)}

                {activeTab === 'Pasadas' &&
                  (classes.length > 0
                    ? (
                      <>
                        {classes.map((_class, index) => <UserClassRow key={`${_class._id}-${index}`} data={_class} />)}
                        {loadingMore && (
                          <div className="flex justify-center py-4">
                            <Loader />
                          </div>
                        )}
                        <div ref={sentinelRef} className="h-4" />
                      </>
                    )
                    : <NoClasses />)}

                {activeTab === 'Calendario' && (
                  <>
                    <div className='text-2xl text-black font-medium text-center'>
                      Has tomado <span className='text-holos-dark'>{classes.length}</span> {classes.length === 1 ? 'clase' : 'clases'} este mes.
                    </div>
                    <CalendarView
                      value={date}
                      onChange={value => {
                        let newDate: Date | null = null;
                        if (Array.isArray(value)) {
                          // If it's a range, pick the first date or null
                          newDate = value[0] ? new Date(value[0] as string | number | Date) : null;
                        } else {
                          newDate = value ? new Date(value as string | number | Date) : null;
                        }
                        // Always update the date for now to test
                        if (newDate) {
                          setDate(newDate);
                        }
                      }}
                      onMonthChange={(newDate) => {
                        setDate(newDate);
                      }}
                      highlightedDates={classes.map(_class => new Date(_class.startTime))}
                    />
                    {classes.length > 0
                      ? (
                        <>
                          {classes.map((_class, index) => <UserClassRow key={`${_class._id}-${index}`} data={_class} />)}
                        </>
                      )
                      : <NoClasses />}
                  </>
                )}

                {activeTab === 'Favoritas' &&
                  (favoritesLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader />
                    </div>
                  ) : favorites.length > 0
                    ? favorites.map(f => (
                      <FavoriteClassRow 
                        key={f._id} 
                        title={f.class.title} 
                        description={f.class.studio.name} 
                        studioSlug={f.class.studio.slug}
                        classSlug={f.class.slug}
                        onUnfavorite={() => handleUnfavorite(f.class._id)} 
                      />
                    ))
                    : <NoClasses />)}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
